import type { ApiResponse } from '@awang/shared-types'

const API_BASE = '/api/v1'

let isRefreshing = false
let refreshPromise: Promise<void> | null = null
const pendingQueue: Array<() => void> = []

async function refreshToken(): Promise<void> {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    throw new Error('缺少刷新令牌')
  }

  const response = await fetch(`${API_BASE}/identity/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  const data = (await response.json()) as ApiResponse<{
    access_token: string
    refresh_token: string
    expires_in: number
  }>

  if (!response.ok || !data.success) {
    throw new Error(data.message || '刷新令牌失败')
  }

  localStorage.setItem('access_token', data.data.access_token)
  localStorage.setItem('refresh_token', data.data.refresh_token)
}

async function doRefresh(): Promise<void> {
  if (!isRefreshing) {
    isRefreshing = true
    refreshPromise = refreshToken()
      .catch(() => {
        // 刷新失败，清除登录态并跳转
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        throw new Error('会话已过期，请重新登录')
      })
      .finally(() => {
        isRefreshing = false
        refreshPromise = null
      })
  }
  if (refreshPromise) {
    return refreshPromise
  }
  throw new Error('刷新令牌失败')
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE}${path}`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  const token = localStorage.getItem('access_token')
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const doRequest = async (): Promise<Response> => {
    return fetch(url, {
      ...options,
      headers,
    })
  }

  let response = await doRequest()

  // 处理 401：尝试自动刷新 Token
  if (response.status === 401 && token) {
    if (isRefreshing) {
      // 等待当前刷新完成后再重试
      await new Promise<void>((resolve) => pendingQueue.push(resolve))
      // 更新 Authorization header
      const newToken = localStorage.getItem('access_token')
      if (newToken) {
        headers.Authorization = `Bearer ${newToken}`
      }
      response = await doRequest()
    } else {
      try {
        await doRefresh()
        const newToken = localStorage.getItem('access_token')
        if (newToken) {
          headers.Authorization = `Bearer ${newToken}`
        }
        response = await doRequest()
      } catch {
        throw new Error('会话已过期，请重新登录')
      } finally {
        // 唤醒等待队列
        pendingQueue.forEach((resolve) => resolve())
        pendingQueue.length = 0
      }
    }
  }

  const data = (await response.json()) as ApiResponse<T>

  if (!response.ok || !data.success) {
    throw new Error(data.message || '请求失败')
  }

  return data.data
}

export const client = {
  get: <T>(path: string): Promise<T> => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
}
